import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Clock, CheckCircle2, TrendingDown, CalendarX, Eye } from "lucide-react";
import { toast } from "sonner";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const today = () => new Date().toISOString().slice(0, 10);
const h = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })} h`;

function Kpi({ label, value, icon: Icon, tone, testid }) {
  return (
    <div className="ek-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#5C736A]"><Icon className={`w-4 h-4 ${tone}`} />{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
    </div>
  );
}

function computeStats(rows) {
  const dues = rows.reduce((s, r) => s + Number(r.heures_dues || 0), 0);
  const real = rows.reduce((s, r) => s + Number(r.heures_realisees || 0), 0);
  return { dues, real, ecart: real - dues, taux: dues ? Math.round((real / dues) * 1000) / 10 : null, absences: rows.filter((r) => r.absent).length };
}

function StatsRow({ s }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
      <Kpi label="Heures dues" value={h(s.dues)} icon={Clock} tone="text-[#5C736A]" testid="kpi-dues" />
      <Kpi label="Heures réalisées" value={h(s.real)} icon={CheckCircle2} tone="text-[#1F7A63]" testid="kpi-realisees" />
      <Kpi label="Écart" value={`${s.ecart > 0 ? "+" : ""}${h(s.ecart)}`} icon={TrendingDown} tone={s.ecart < 0 ? "text-red-600" : "text-[#1F7A63]"} testid="kpi-ecart" />
      <Kpi label="Taux de réalisation" value={s.taux == null ? "—" : `${s.taux} %`} icon={CheckCircle2} tone="text-[#B38F00]" testid="kpi-taux" />
      <Kpi label="Absences" value={s.absences} icon={CalendarX} tone="text-red-600" testid="kpi-absences" />
    </div>
  );
}

function EntriesTable({ rows, onDelete }) {
  return (
    <div className="ek-table-wrap">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Date</TableHead><TableHead>Dues</TableHead><TableHead>Réalisées</TableHead><TableHead>Statut</TableHead><TableHead>Motif / note</TableHead>
          {onDelete && <TableHead className="w-12" />}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} data-testid="hours-row">
              <TableCell className="whitespace-nowrap">{new Date(r.date).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short" })}</TableCell>
              <TableCell>{h(r.heures_dues)}</TableCell>
              <TableCell className="font-semibold">{h(r.heures_realisees)}</TableCell>
              <TableCell>
                <span className="flex items-center gap-1.5 text-xs"><span className={`w-2.5 h-2.5 rounded-full ${r.absent ? "bg-red-500" : "bg-[#1F7A63]"}`} />{r.absent ? "Absent" : "Présent"}</span>
              </TableCell>
              <TableCell className="text-sm text-[#5C736A]">{r.motif || "—"}</TableCell>
              {onDelete && <TableCell><Button size="icon" variant="ghost" onClick={() => onDelete(r)} data-testid="hours-delete-btn"><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell>}
            </TableRow>
          ))}
          {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-[#5C736A] py-8">Aucune saisie pour ce mois.</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}

function EntryForm({ onSaved }) {
  const empty = { date: today(), heures_dues: "", heures_realisees: "", absent: false, motif: "" };
  const [f, setF] = useState(empty);
  const submit = async () => {
    if (f.heures_dues === "") return toast.error("Indiquez les heures dues");
    try {
      await api.post("/espace/hours", { ...f, heures_dues: Number(f.heures_dues), heures_realisees: f.absent ? 0 : Number(f.heures_realisees || 0), motif: f.motif || null });
      toast.success("Heures enregistrées");
      setF(empty);
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="ek-card p-4 grid grid-cols-2 lg:grid-cols-6 gap-3 items-end" data-testid="hours-form">
      <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="hours-date" /></div>
      <div><Label>Heures dues</Label><Input type="number" min="0" max="24" step="0.5" value={f.heures_dues} onChange={(e) => setF({ ...f, heures_dues: e.target.value })} data-testid="hours-dues" /></div>
      <div><Label>Heures réalisées</Label><Input type="number" min="0" max="24" step="0.5" disabled={f.absent} value={f.absent ? 0 : f.heures_realisees} onChange={(e) => setF({ ...f, heures_realisees: e.target.value })} data-testid="hours-realisees" /></div>
      <div className="flex items-center gap-2 pb-2"><Switch checked={f.absent} onCheckedChange={(v) => setF({ ...f, absent: v })} data-testid="hours-absent" /><Label>Absent</Label></div>
      <div><Label>Motif / note</Label><Input value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} placeholder="Optionnel" data-testid="hours-motif" /></div>
      <Button onClick={submit} className="bg-[#1F7A63] hover:bg-[#165c4b] text-white" data-testid="hours-submit"><Plus className="w-4 h-4 mr-1" /> Enregistrer</Button>
    </div>
  );
}

function TeacherView({ month }) {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/espace/hours", { params: { month } }).then(({ data }) => setRows(data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);
  const del = async (r) => {
    if (!window.confirm("Supprimer cette saisie ?")) return;
    try { await api.delete(`/espace/hours/${r.id}`); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="space-y-4">
      <StatsRow s={computeStats(rows)} />
      <EntryForm onSaved={load} />
      <div className="ek-card p-0"><EntriesTable rows={rows} onDelete={del} /></div>
    </div>
  );
}

function ManagerView({ month }) {
  const [summary, setSummary] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    api.get("/espace/hours/summary", { params: { month } }).then(({ data }) => setSummary(data)).catch((e) => toast.error(formatError(e)));
  }, [month]);
  const open = async (t) => {
    try {
      const { data } = await api.get("/espace/hours", { params: { month, user_id: t.user_id } });
      setDetail({ t, rows: data });
    } catch (e) { toast.error(formatError(e)); }
  };
  const total = summary.reduce((s, t) => ({ dues: s.dues + t.dues, real: s.real + t.realisees, absences: s.absences + t.absences }), { dues: 0, real: 0, absences: 0 });
  return (
    <div className="space-y-4">
      <StatsRow s={{ ...total, ecart: total.real - total.dues, taux: total.dues ? Math.round((total.real / total.dues) * 1000) / 10 : null }} />
      <div className="ek-card p-0 ek-table-wrap" data-testid="hours-summary-table">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Enseignant</TableHead><TableHead>Dues</TableHead><TableHead>Réalisées</TableHead><TableHead>Écart</TableHead><TableHead>Taux</TableHead><TableHead>Absences</TableHead><TableHead className="w-12" />
          </TableRow></TableHeader>
          <TableBody>
            {summary.map((t) => (
              <TableRow key={t.user_id} data-testid="hours-summary-row">
                <TableCell className="font-semibold">{t.name}</TableCell>
                <TableCell>{h(t.dues)}</TableCell>
                <TableCell>{h(t.realisees)}</TableCell>
                <TableCell className={t.ecart < 0 ? "text-red-600 font-semibold" : "text-[#1F7A63] font-semibold"}>{t.ecart > 0 ? "+" : ""}{h(t.ecart)}</TableCell>
                <TableCell>{t.taux == null ? "—" : `${t.taux} %`}</TableCell>
                <TableCell>{t.absences}</TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => open(t)} title="Voir le détail" data-testid="hours-summary-view"><Eye className="w-4 h-4" /></Button></TableCell>
              </TableRow>
            ))}
            {summary.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-sm text-[#5C736A] py-8">Aucun enseignant.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{detail?.t.name} — {month}</DialogTitle></DialogHeader>
          {detail && <EntriesTable rows={detail.rows} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function CantumHoraire() {
  const { user } = useAuth();
  const [month, setMonth] = useState(thisMonth());
  const isManager = ["admin", "directeur"].includes(user?.role);
  return (
    <div className="space-y-4" data-testid="cantum-horaire">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-[#5C736A]">
          {isManager ? "Suivi des heures saisies par vos enseignants." : "Saisissez chaque jour vos heures dues et réalisées. Votre direction les voit directement."}
        </p>
        <div className="flex items-center gap-2"><Label>Mois</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="hours-month" /></div>
      </div>
      {isManager ? <ManagerView month={month} /> : <TeacherView month={month} />}
    </div>
  );
}
